import React, { useEffect, useState } from 'react';
import { PageHeader } from '@/components/primitives';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

export default function CompanySettingsPage() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);

  useEffect(() => { api.get(`/companies/${companyId}`).then((r) => setCompany(r.data)); }, [companyId]);

  if (!company) return null;

  const update = (patch) => setCompany({ ...company, ...patch });

  const save = async () => {
    try {
      await api.patch(`/companies/${companyId}`, {
        name: company.name, industry: company.industry, address: company.address, gst: company.gst, pan: company.pan,
        directors: company.directors || [], default_cc_emails: company.default_cc_emails || [], notes: company.notes,
      });
      toast.success('Saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const directorsStr = (company.directors || []).map((d) => `${d.name} | ${d.email} | ${d.role}`).join('\n');
  const ccStr = (company.default_cc_emails || []).join(', ');

  return (
    <>
      <PageHeader
        title={`${company.name} · Settings`}
        description="Company profile, directors and default CC recipients used on every outbound email."
        actions={<Button onClick={save} data-testid="company-settings-save"><Save className="h-4 w-4 mr-2" />Save</Button>}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-3xl grid gap-4">
        <div className="space-y-1.5"><Label>Company name</Label><Input value={company.name || ''} onChange={(e) => update({ name: e.target.value })} /></div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Industry</Label><Input value={company.industry || ''} onChange={(e) => update({ industry: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>GST</Label><Input value={company.gst || ''} onChange={(e) => update({ gst: e.target.value })} /></div>
        </div>
        <div className="space-y-1.5"><Label>PAN</Label><Input value={company.pan || ''} onChange={(e) => update({ pan: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>Address</Label><Textarea rows={2} value={company.address || ''} onChange={(e) => update({ address: e.target.value })} /></div>
        <div className="space-y-1.5">
          <Label>Directors / Managers / Concerned persons</Label>
          <Textarea rows={4} placeholder={'Name | email | Role'} defaultValue={directorsStr} onBlur={(e) => {
            const arr = e.target.value.split('\n').map((l) => l.trim()).filter(Boolean).map((line) => {
              const [name, email, role] = line.split('|').map((s) => (s || '').trim());
              return { name: name || 'Director', email: email || '', role: role || 'Director' };
            }).filter((d) => d.email);
            update({ directors: arr });
          }} data-testid="company-directors" />
          <p className="text-xs text-muted-foreground">One per line, separated by <span className="font-mono">|</span></p>
        </div>
        <div className="space-y-1.5">
          <Label>Default CC emails</Label>
          <Textarea rows={2} defaultValue={ccStr} onBlur={(e) => update({ default_cc_emails: e.target.value.split(/[\n,]/).map((s) => s.trim()).filter(Boolean) })} data-testid="company-cc-emails" />
          <p className="text-xs text-muted-foreground">These emails are auto-added as CC on every outbound email for this company.</p>
        </div>
        <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={3} value={company.notes || ''} onChange={(e) => update({ notes: e.target.value })} /></div>
      </div>
    </>
  );
}
